import {
  createRoute,
  Navigate,
  redirect,
  useSearch,
} from '@tanstack/react-router'
import { type ComponentProps, lazy } from 'react'
import type { AllDocsPage as AllDocsPageType } from '../../routes/studio/AllDocsPage'
import { Notice, Page } from '../../ui'
import { bookRoutes } from '../book-routes'
import { listSearch } from '../list-search'
import { useOrg } from '../org'
import { currentSite } from '../public-router'
import type { LazyPage } from './lazy-page'
import { guardAt } from './products'
import { rootRoute } from './root'
import '../../routes/studio/studio-deck-client'

const AllDocsPage: LazyPage<ComponentProps<typeof AllDocsPageType>> = lazy(() =>
  import('../../routes/studio/AllDocsPage').then((m) => ({
    default: m.AllDocsPage,
  })),
)
const PagesPage: LazyPage<{ pageKey: string; recordSpaceId?: string }> = lazy(
  () =>
    import('../../routes/studio/PagesPage').then((m) => ({
      default: m.PagesPage,
    })),
)
const BookCoverPage: LazyPage<{ coverKey: string }> = lazy(() =>
  import('../../routes/studio/BookCoverPage').then((m) => ({
    default: m.BookCoverPage,
  })),
)
const DecksPage: LazyPage<{ deckKey: string; recordSpaceId?: string }> = lazy(
  () =>
    import('../../routes/studio/DecksPage').then((m) => ({
      default: m.DecksPage,
    })),
)
const CanvasPage: LazyPage<{ boardKey: string; recordSpaceId?: string }> = lazy(
  () =>
    import('../../routes/studio/CanvasPage').then((m) => ({
      default: m.CanvasPage,
    })),
)
const RecentlyDeletedPage: LazyPage = lazy(() =>
  import('../../routes/studio/RecentlyDeletedPage').then((m) => ({
    default: m.RecentlyDeletedPage,
  })),
)
const ActivityPage: LazyPage = lazy(() =>
  import('../../routes/studio/ActivityPage').then((m) => ({
    default: m.ActivityPage,
  })),
)
const MediaPage: LazyPage = lazy(() =>
  import('../../routes/studio/MediaPage').then((m) => ({
    default: m.MediaPage,
  })),
)
const LibraryPage: LazyPage = lazy(() =>
  import('../../routes/studio/LibraryPage').then((m) => ({
    default: m.LibraryPage,
  })),
)
const FontWorkbench: LazyPage = lazy(() =>
  import('../../routes/studio/FontWorkbench').then((m) => ({
    default: m.FontWorkbench,
  })),
)
const SourcePage: LazyPage<{ sourceId: string }> = lazy(() =>
  import('../../routes/studio/SourcePage').then((m) => ({
    default: m.SourcePage,
  })),
)
const BookPage: LazyPage<{ bookId: string; beside?: string }> = lazy(() =>
  import('../../routes/studio/BookPage').then((m) => ({
    default: m.BookPage,
  })),
)
const ModaahSection: LazyPage<{ spaceId: string }> = lazy(() =>
  import('../../routes/modaah/ModaahSection').then((m) => ({
    default: m.ModaahSection,
  })),
)
const ModaahComposer: LazyPage<{ id: string | null }> = lazy(() =>
  import('../../routes/modaah/ModaahComposer').then((m) => ({
    default: m.ModaahComposer,
  })),
)
/** The shul's printed pages — `modaah` stays the code identifier. */
function ModaahPage() {
  const { spaceId } = useOrg()
  return (
    <Page>
      {spaceId ? (
        <ModaahSection spaceId={spaceId} />
      ) : (
        <Notice tone="error">No congregation selected.</Notice>
      )}
    </Page>
  )
}
/* A source outlives every book that quotes it, so it is reached from the
   nav and opens on its own rather than through the thing citing it. */
const otzarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/otzar',
  beforeLoad: guardAt('/otzar'),
  component: LibraryPage,
})
/* Otzar's earlier address, kept answering because a link to it may already
   be saved somewhere. */
const libraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/library',
  beforeLoad: () => {
    throw redirect({ to: '/otzar' })
  },
})
const pagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pages',
  beforeLoad: ({ search }) => {
    guardAt('/pages')()
    if (currentSite() === 'studio')
      throw redirect({ to: '/documents', search: { ...search, type: 'page' } })
  },
  validateSearch: listSearch,
  component: ModaahPage,
})
const pageNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pages/new',
  beforeLoad: () => {
    guardAt('/pages')()
    /* A Studio page is written by an agent over the tool bridge, so there is
       no page here to start one on; the address lands on the documents
       instead. */
    if (currentSite() === 'studio') throw redirect({ to: '/documents' })
  },
  component: () => <ModaahComposer id={null} />,
})
const pageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pages/$key',
  beforeLoad: guardAt('/pages'),
  validateSearch: (raw: Record<string, unknown>) => ({
    ...(typeof raw.spaceId === 'string' ? { spaceId: raw.spaceId } : {}),
  }),
  component: () => {
    const { key } = pageRoute.useParams()
    const { spaceId } = pageRoute.useSearch()
    return currentSite() === 'studio' ? (
      <PagesPage pageKey={key} recordSpaceId={spaceId} />
    ) : (
      <ModaahComposer id={key} />
    )
  },
})
const bookCoverRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/book-covers/$key',
  beforeLoad: guardAt('/documents'),
  component: () => {
    const { key } = bookCoverRoute.useParams()
    return <BookCoverPage coverKey={key} />
  },
})
const decksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/decks',
  beforeLoad: ({ search }) => {
    guardAt('/decks')()
    throw redirect({ to: '/documents', search: { ...search, type: 'deck' } })
  },
  validateSearch: listSearch,
})
const deckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/decks/$key',
  beforeLoad: guardAt('/decks'),
  validateSearch: (raw: Record<string, unknown>) => ({
    ...(typeof raw.spaceId === 'string' ? { spaceId: raw.spaceId } : {}),
  }),
  component: () => {
    const { key } = deckRoute.useParams()
    const { spaceId } = deckRoute.useSearch()
    return <DecksPage deckKey={key} recordSpaceId={spaceId} />
  },
})
/* A Studio board answers at `/canvases`, apart from a shul's screens at
   `/board`. */
const canvasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/canvases',
  beforeLoad: ({ search }) => {
    guardAt('/canvases')()
    throw redirect({ to: '/documents', search: { ...search, type: 'board' } })
  },
  validateSearch: listSearch,
})
const canvasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/canvases/$key',
  beforeLoad: guardAt('/canvases'),
  validateSearch: (raw: Record<string, unknown>) => ({
    ...(typeof raw.spaceId === 'string' ? { spaceId: raw.spaceId } : {}),
  }),
  component: () => {
    const { key } = canvasRoute.useParams()
    const { spaceId } = canvasRoute.useSearch()
    return <CanvasPage boardKey={key} recordSpaceId={spaceId} />
  },
})
/* Reached from the nav rather than from a book: a face is worked on before
   anything is set in it, and the same face serves every book. */
const fontRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/type',
  beforeLoad: guardAt('/type'),
  component: FontWorkbench,
})
const sourceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/otzar/$id',
  beforeLoad: guardAt('/otzar'),
  component: function SourceRoute() {
    const { id } = sourceRoute.useParams()
    return <SourcePage sourceId={id} />
  },
})
const librarySourceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/library/$id',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/otzar/$id', params: { id: params.id } })
  },
})

/*
 * All documents, and its two narrower views. Each is an address of its own, so
 * a view is a tab in the bar and a link somebody can send.
 */
const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <AllDocsPage tab="all" />,
})
const docsRecentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/recent',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <AllDocsPage tab="recent" />,
})
const docsSharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/shared',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <AllDocsPage tab="shared" />,
})
const docsUnfiledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/unfiled',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <AllDocsPage tab="unfiled" />,
})
/* What this Studio has deleted and may still put back, reached from All
   documents' Restore action. */
const docsDeletedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/deleted',
  beforeLoad: guardAt('/documents'),
  component: () => <RecentlyDeletedPage />,
})
/* Who was let in to a space's things, its public links, and what moved in
   or out — the space's holder's, as its bin is, reached from the sidebar. */
const docsActivityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/activity',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <ActivityPage />,
})
const docsFoldersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/folders',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <AllDocsPage tab="folders" />,
})
const docsFolderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/folder/$id',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: function DocsFolder() {
    const { id } = docsFolderRoute.useParams()
    return <AllDocsPage tab="folder" folderId={id} />
  },
})
/* What other people share with this person, one document at a time, and a
   folder they share, browsed as its owner arranged it. */
const docsWithMeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/shared-with-me',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: () => <AllDocsPage tab="with-me" />,
})
const docsSharedFolderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/shared-folder/$id',
  beforeLoad: guardAt('/documents'),
  validateSearch: listSearch,
  component: function DocsSharedFolder() {
    const { id } = docsSharedFolderRoute.useParams()
    return <AllDocsPage tab="shared-folder" folderId={id} />
  },
})

/* The images and sounds a space's documents use, uploaded once. */
const mediaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/media',
  beforeLoad: guardAt('/media'),
  validateSearch: listSearch,
  component: () => <MediaPage />,
})

/**
 * The shelf's address is in mail already sent — an offer's link carries
 * `?offers=1` — so it stays, and lands on All documents narrowed to books with
 * everything after the `?` intact.
 */
function ShelfToDocs() {
  const search = useSearch({ strict: false }) as Record<string, string>
  return (
    <Navigate to="/documents" search={{ ...search, type: 'book' }} replace />
  )
}

export const studioBookRoutes = bookRoutes(rootRoute, {
  Shelf: ShelfToDocs,
  Book: BookPage,
  guard: guardAt('/books'),
})

export const studioShelfRoutes = [
  docsRoute,
  docsRecentRoute,
  docsSharedRoute,
  docsUnfiledRoute,
  docsDeletedRoute,
  docsActivityRoute,
  docsFoldersRoute,
  docsFolderRoute,
  docsWithMeRoute,
  docsSharedFolderRoute,
  mediaRoute,
  pagesRoute,
  pageNewRoute,
  pageRoute,
  bookCoverRoute,
  decksRoute,
  deckRoute,
  canvasesRoute,
  canvasRoute,
  otzarRoute,
  libraryRoute,
]

export const studioTypeRoutes = [fontRoute, sourceRoute, librarySourceRoute]
